// @ts-nocheck
/*
 * Port of views/dashboard/ivr/IVR/create.js (origin/main-dev, 1273 lines).
 * BYTE-IDENTICAL — this is the IVR editor canvas: palette (left) + toolbar
 * (top) + ReactFlow (right). All node/edge JSON is produced by
 * Helpers/utils.ts's transformNodes; the toolbar's Save button posts the
 * JSON directly to API_IVR_CREATE.
 *
 * V1 quirks preserved verbatim:
 *  - `keys` height math for gatherNode in tidyLayout uses selectedHandles
 *    count + numDigits !== 1 default-row hint (drives dagre height so
 *    edges land on the right handle Y-offsets).
 *  - MiniMap colour map has no conditionalNode entry (falls back to grey)
 *    — inherited from v1.
 *  - `updateNodeData` intercepts numDigits changes and prunes the
 *    conflicting single/multi handle edges BEFORE merging the update.
 *  - Alt+letter shortcut listener uses e.code (physical), guarded from
 *    input/textarea/select/contentEditable AND from any open modal
 *    (`.ivr-drawer-backdrop, .modal.show`).
 *  - The whole page neutralises global `html { zoom }` on mount because
 *    display-scale zoom breaks ReactFlow's pointer math for handle drags.
 *  - Clone mode: when `?cloneId=` is present in query, the initial
 *    answer-node auto-add is SKIPPED and instead the clone source's
 *    nodes_data/edges_data/scheduleId are hydrated.
 *
 * Library swap: v1 `reactflow` v11 â†’ v2 `@xyflow/react` v12 (already in
 * use across all node ports). Node/edge JSON shape is identical.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import dagre from 'dagre';
import {
  PhoneIncoming,
  Volume2,
  Grid3X3,
  PhoneForwarded,
  PhoneOff,
  Mic,
  Voicemail as VoicemailIcon,
  PhoneMissed,
  SlidersHorizontal,
  Webhook as WebhookIcon,
  RotateCcw,
  Globe,
  Info,
  GitBranch,
  Bot,
  Wand2,
  Download,
  ChevronDown,
  Image as ImageIcon,
  FileText,
  Code2,
  Lock,
  LockOpen,
  Calendar as CalendarIcon,
  Loader2,
  Save,
  Clock,
  AlertTriangle,
} from 'lucide-react';
import AnswerNode from './IVR/nodes/AnswerNode';
import DtmfNode from './IVR/nodes/DtmfNode';
import HangupNode from './IVR/nodes/HangupNode';
import PlayNode from './IVR/nodes/PlayNode';
import TransferNode from './IVR/nodes/TransferNode';
import RecordNode from './IVR/nodes/RecordNode';
import VoicemailNode from './IVR/nodes/VoicemailNode';
import MissedNode from './IVR/nodes/MissedNode';
import DefaultNode from './IVR/nodes/DefaultNode';
import ApiNode from './IVR/nodes/ApiNode';
import LanguageNode from './IVR/nodes/LanguageNode';
import ConditionalNode from './IVR/nodes/ConditionalNode';
import AiAgentNode from './IVR/nodes/AiAgentNode';
import CustomEdge from './IVR/edges/ButtonEdge';
import {
  ReactFlow,
  addEdge,
  MarkerType,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  Background,
  Controls,
  MiniMap,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  getApiCall,
  postApiCall,
  fetchAndSetFlashMessage,
} from '@/service/httpCall';
import {
  API_IVR_SETTINGS,
  API_IVR_CREATE,
  API_IVR_EDIT,
  API_IVR_VIEW,
  API_IVR_DRAFT_CREATE,
  API_IVR_DRAFT_UPDATE,
  API_SMSWEBHOOK_LIST,
  API_WEBHOOK_LIST,
  API_APIWEBHOOK_LIST,
  API_FLOW_VARIABLES_LIST,
  API_FLOW_OPERATORS_LIST,
} from '@/service/url';
import {
  DTMF_KEYS,
  ACTION_COLORS,
  IVR_SETTING_ORDER,
} from '@/utilities/constant';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { NodesContext } from './IVR/provider/IvrContext';
import {
  transformNodes,
  validateIVR,
  getAcyclicLayoutEdges,
  fetchIsBackendDev,
  downloadIvrFlowJson,
  aiBranchConnectError,
  normalizeConditionGroups,
  conditionBranchLabel,
} from './IVR/Helpers/utils';
import {
  BACKEND_API_BASE_URL,
  FLASH_MESSAGE_ACTION_OBJECT,
  MODULE_ACL,
  USER_ROLES,
} from '@/utilities/constant';
import { getUser, Storage } from '@/utilities/setting';
import {
  canAccessMissedIvr,
  canUseIvrPaletteAction,
  getUserModuleOperations,
} from '@/utilities/ivrAcl';
import { WEEK_DAYS, ROUTES, DEFAULT_SCHEDULE_START, DEFAULT_SCHEDULE_END } from '@/utilities/constant';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useSidebarPosition } from '@/router/context/SidebarPositionContext';
import '@/assets/custom/css/ivr-editor.css';
import { Button as _ShadButton } from '@/components/ui/button';
import { CalendarTimeInput } from '@/components/ui/calendar-inputs';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts = [];
  if (md) parts.push(`col-md-${md}`);
  if (lg) parts.push(`col-lg-${lg}`);
  if (xl) parts.push(`col-xl-${xl}`);
  if (sm) parts.push(`col-sm-${sm}`);
  if (xs) parts.push(`col-${xs}`);
  if (!parts.length) parts.push('col');
  return parts.join(' ');
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
};
const Button: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <_ShadButton variant={mapped} size={size} className={className} {...props}>
      {children}
    </_ShadButton>
  );
};
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx('form-control form-select', isInvalid && 'is-invalid', className)}
    {...props}
  >
    {children}
  </select>
);
Form.Check = ({
  type = 'checkbox',
  label,
  id,
  className,
  inline,
  children,
  ...props
}: any) => (
  <div className={_cx('form-check', inline && 'form-check-inline', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
    {children}
  </div>
);
const Modal: any = ({ show, onHide, size, centered: _c, children, className, ...props }: any) => {
  const sizeCls =
    size === 'sm'
      ? 'sm:max-w-md'
      : size === 'lg'
      ? 'sm:max-w-2xl'
      : size === 'xl'
      ? 'sm:max-w-6xl'
      : '';
  return (
    <Dialog open={!!show} onOpenChange={(o: boolean) => !o && onHide?.()}>
      <DialogContent
        className={`${sizeCls} max-h-[90vh] overflow-y-auto ${className || ''}`.trim()}
        {...props}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
};
Modal.Header = ({ children, closeButton: _cb, className, ...props }: any) => (
  <DialogHeader className={className} {...props}>
    {children}
  </DialogHeader>
);
Modal.Title = ({ children, className, ...props }: any) => (
  <DialogTitle className={className} {...props}>
    {children}
  </DialogTitle>
);
Modal.Body = ({ children, className, ...props }: any) => (
  <div className={`overflow-x-auto ${className || ''}`.trim()} {...props}>
    {children}
  </div>
);
Modal.Footer = ({ children, className, ...props }: any) => (
  <DialogFooter className={className} {...props}>
    {children}
  </DialogFooter>
);

const nodeTypes = {
  answerNode: AnswerNode,
  playNode: PlayNode,
  gatherNode: DtmfNode,
  transferNode: TransferNode,
  hangupNode: HangupNode,
  recordNode: RecordNode,
  voicemailNode: VoicemailNode,
  missedNode: MissedNode,
  defaultNode: DefaultNode,
  apiNode: ApiNode,
  languageNode: LanguageNode,
  conditionalNode: ConditionalNode,
  aiAgentNode: AiAgentNode,
};
const edgeTypes = { custom: CustomEdge };

// Keyed by e.code (physical key), not e.key — keeps Alt+letter reliable on
// Mac where Option+letter composes an accented/special character in e.key.
const NODE_SHORTCUT_MAP: any = {
  KeyP: 'play',
  KeyG: 'gather', // "D" is taken by the browser's address-bar focus shortcut
  KeyL: 'language',
  KeyV: 'voicemail',
  KeyH: 'hangup',
  KeyR: 'record',
  KeyT: 'transfer',
  KeyA: 'api',
  KeyC: 'conditional',
};

const NODE_SHORTCUT_LIST = Object.entries(NODE_SHORTCUT_MAP).map(
  ([code, type]) => ({
    type,
    keyLabel: code.replace('Key', ''),
  }),
);

const NODE_PALETTE_CFG: any = {
  answer: { Icon: PhoneIncoming, color: '#059669', label: 'Answer', hint: 'Call entry point' },
  play: { Icon: Volume2, color: '#2563eb', label: 'Play Audio', hint: 'Play a message' },
  gather: { Icon: Grid3X3, color: '#7c3aed', label: 'DTMF', hint: 'Collect digits' },
  transfer: { Icon: PhoneForwarded, color: '#d97706', label: 'Transfer', hint: 'Route the call' },
  hangup: { Icon: PhoneOff, color: '#dc2626', label: 'Hangup', hint: 'End the call' },
  record: { Icon: Mic, color: '#0891b2', label: 'Record', hint: 'Record audio' },
  voicemail: { Icon: VoicemailIcon, color: '#4f46e5', label: 'Voicemail', hint: 'Leave voicemail' },
  missed: { Icon: PhoneMissed, color: '#9333ea', label: 'Missed', hint: 'Missed call flow' },
  default: { Icon: SlidersHorizontal, color: '#475569', label: 'Default', hint: 'Fallback action' },
  api: { Icon: WebhookIcon, color: '#b45309', label: 'API Webhook', hint: 'HTTP API call' },
  language: { Icon: Globe, color: '#0d9488', label: 'Language', hint: 'Route by language' },
  conditional: { Icon: GitBranch, color: '#c026d3', label: 'Conditional', hint: 'Branch by condition' },
  aiAgent: { Icon: Bot, color: '#0284c7', label: 'AI Agent', hint: 'Hand call to AI agent' },
};

const NewAddIVR = ({ app }: any) => {
  const flowRef = useRef<any>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState<any>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<any>([]);
  const [nodeConnections, setNodeConnections] = useState<any>({});
  const [flowName, setFlowName] = useState('');
  const [actions, setActions] = useState<any>({});
  const [error, setError] = useState('');
  const [isGatherNode, setIsGatherNode] = useState(0);
  const [alertMessage, setAlertMessage] = useState('');

  const [smsWebhookFlag, setSmsWebhookFlag] = useState(false);
  const [smsWebhookList, setSmsWebhookList] = useState<any[]>([]);
  const [webhookList, setWebhookList] = useState<any[]>([]);
  const [apiWebhookList, setApiWebhookList] = useState<any[]>([]);
  const [flowVariablesList, setFlowVariablesList] = useState<any[]>([]);
  const [flowOperatorsList, setFlowOperatorsList] = useState<any[]>([]);

  const [audioList, setAudioList] = useState<any[]>([]);
  const [isLocked, setIsLocked] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showShortcutsModal, setShowShortcutsModal] = useState(false);
  const [isBackendDev, setIsBackendDev] = useState(false);

  const [timeScheduleModel, setTimeScheduleModel] = useState(false);
  const [startTime, setStartTime] = useState(DEFAULT_SCHEDULE_START);
  const [endTime, setEndTime] = useState(DEFAULT_SCHEDULE_END);
  const [weekDays, setWeekDays] = useState<any[]>([...WEEK_DAYS]);
  const [isWholeDay, setIsWholeDay] = useState(true);
  const [isWholeWeek, setIsWholeWeek] = useState(true);
  const [scheduleData, setScheduleData] = useState<any>({});
  const [loggedInUser, setLoggedInUser] = useState<any>(null);
  const [previousTime, setPreviousTime] = useState<any>({ start: '', end: '' });
  const [previousWeekDays, setPreviousWeekDays] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Draft autosave state — set once the flow name creates the draft row.
  const [draftId, setDraftId] = useState<string | null>(null);
  const [draftScheduleId, setDraftScheduleId] = useState<any>(null);
  const draftCreateInFlightRef = useRef(false);
  const draftSaveTimeoutRef = useRef<any>(null);

  const { cid } = useParams();
  const navigate = useNavigate();
  const { position: sidebarPosition } = useSidebarPosition() as any;
  const nodeBarOnLeft = sidebarPosition === 'right';
  const [searchParams] = useSearchParams();
  const categoryParam = (
    searchParams.get('category') || 'normal'
  ).toLowerCase();
  const isMissed = categoryParam === 'missed';
  const cloneId = searchParams.get('cloneId');

  const ivrAclContext = useMemo(() => {
    try {
      const raw = Storage.getItem('user');
      if (!raw) return { userRole: null, isSuperAdmin: false };
      const user = JSON.parse(raw);
      return {
        userRole: user?.role,
        isSuperAdmin: user?.role === USER_ROLES.SUPERADMIN,
      };
    } catch {
      return { userRole: null, isSuperAdmin: false };
    }
  }, []);

  const userModuleOps = useMemo(() => {
    try {
      const raw = Storage.getItem('user');
      if (!raw) return {};
      return getUserModuleOperations(JSON.parse(raw));
    } catch {
      return {};
    }
  }, []);

  const isPaletteActionAllowed = useCallback(
    (type: any) => {
      if (isMissed) {
        return (
          type === 'missed' &&
          canAccessMissedIvr(userModuleOps, ivrAclContext)
        );
      }
      return canUseIvrPaletteAction(userModuleOps, type, ivrAclContext);
    },
    [isMissed, userModuleOps, ivrAclContext],
  );

  useEffect(() => {
    if (!isMissed) return;
    if (!canAccessMissedIvr(userModuleOps, ivrAclContext)) {
      toast.error(
        'You do not have permission to create Missed Type IVR flows.',
      );
      navigate(`/${ROUTES.IVR.LIST.replace(':cid', cid)}`);
    }
  }, [isMissed, userModuleOps, ivrAclContext, cid, navigate]);

  useEffect(() => {
    const resp = getUser();
    if (resp) {
      setLoggedInUser(resp.id);
      getAudioPrompt(resp.id);
    }
    getIvrSettings();
    getSmsWebhookList();
    getWebhookList();
    getApiWebhookList();
    getFlowVariablesList();
    getFlowOperatorsList();
    fetchAndSetFlashMessage(
      (msg: any) =>
        msg.module_name === MODULE_ACL.IVR &&
        msg.action === FLASH_MESSAGE_ACTION_OBJECT.ADD &&
        msg.type === 'Flash Message',
      setAlertMessage,
    );
  }, []);

  // Neutralize global display-scale zoom on IVR editor page.
  useEffect(() => {
    const prevBodyZoom = document.body.style.zoom;
    const prevHtmlZoom = document.documentElement.style.zoom;
    const prevAppZoom =
      document.documentElement.style.getPropertyValue('--app-zoom');
    document.body.style.zoom = '1';
    document.documentElement.style.zoom = '1';
    document.documentElement.style.setProperty('--app-zoom', '1');
    return () => {
      document.body.style.zoom = prevBodyZoom;
      document.documentElement.style.zoom = prevHtmlZoom;
      if (prevAppZoom) {
        document.documentElement.style.setProperty('--app-zoom', prevAppZoom);
      } else {
        document.documentElement.style.removeProperty('--app-zoom');
      }
    };
  }, []);

  useEffect(() => {
    if (!loggedInUser) return;
    if (cloneId) return;
    if (nodes.length > 0) return;
    addNewNode(isMissed ? 'missed' : 'answer');
  }, [loggedInUser, cloneId]);

  useEffect(() => {
    if (!cloneId) return;
    const fetchCloneSource = async () => {
      try {
        const apiIvrViewUrl = API_IVR_VIEW.replace(':cid', cid).replace(
          ':id',
          cloneId,
        );
        const res: any = await getApiCall(apiIvrViewUrl);
        const nodesData = res.nodes_data ? JSON.parse(res.nodes_data) : [];
        const clonedNodes = Object.values(nodesData.nodes || {}).map(
          (item: any) => item.flowRender,
        );
        setNodes(clonedNodes);
        setEdges(res.edges_data ? JSON.parse(res.edges_data) : []);
        setScheduleData(res?.scheduleId || {});
        setIsGatherNode(res.is_gather_node ?? 0);
      } catch (err) {
        console.error(err);
        toast.error('Unable to load the IVR to clone');
      }
    };
    fetchCloneSource();
  }, [cloneId, cid]);

  const getAudioPrompt = async (user_id: any) => {
    const resp: any = await getApiCall(
      `${BACKEND_API_BASE_URL}/v2/prompt?user_id=${user_id}`,
    );
    const welcomeFiles = resp.filter(
      (item: any) =>
        item.prompt_category === 'general' &&
        Array.isArray(item.prompt_url) &&
        item.prompt_url.some((e: any) => e.status === 1),
    );
    setAudioList(welcomeFiles);
  };

  const getIvrSettings = async () => {
    const resp: any = await getApiCall(API_IVR_SETTINGS);
    const rawActions = resp?.actions || {};

    const allActions: any = { ...rawActions };
    if (!allActions.voicemail) allActions.voicemail = 'Voicemail';
    if (!allActions.missed) allActions.missed = 'Missed';
    if (!allActions.api) allActions.api = 'API';
    if (!allActions.language) allActions.language = 'Language';
    if (!allActions.conditional) allActions.conditional = 'Conditional';
    if (!allActions.aiAgent) allActions.aiAgent = 'AI Agent';

    const orderedActions: any = {};
    IVR_SETTING_ORDER.forEach((key: any) => {
      if (allActions[key]) orderedActions[key] = allActions[key];
    });

    setActions(orderedActions);
  };

  const getSmsWebhookList = async () => {
    const resp: any = await getApiCall(API_SMSWEBHOOK_LIST);
    setSmsWebhookFlag(true);
    setSmsWebhookList(resp);
  };

  const getWebhookList = async () => {
    try {
      const resp: any = await getApiCall(API_WEBHOOK_LIST);
      setWebhookList(Array.isArray(resp) ? resp : []);
    } catch (err) {
      console.log(err);
    }
  };

  const getApiWebhookList = async () => {
    try {
      const resp: any = await getApiCall(API_APIWEBHOOK_LIST);
      setApiWebhookList(Array.isArray(resp) ? resp : []);
    } catch (err) {
      console.log(err);
    }
  };

  const getFlowVariablesList = async () => {
    try {
      const resp: any = await getApiCall(API_FLOW_VARIABLES_LIST);
      setFlowVariablesList(Array.isArray(resp) ? resp : []);
    } catch (err) {
      console.log(err);
    }
  };

  const getFlowOperatorsList = async () => {
    try {
      const resp: any = await getApiCall(API_FLOW_OPERATORS_LIST);
      setFlowOperatorsList(Array.isArray(resp) ? resp : []);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    updatedConnections();
  }, [edges]);

  const updatedConnections = () => {
    const result = edges.reduce(
      (acc: any, { source, target, sourceHandle }: any) => {
        const key = sourceHandle ?? source;
        if (!acc[key]) acc[key] = [];
        acc[key].push(target);
        return acc;
      },
      {},
    );
    setNodeConnections(result);
  };

  const generateTimeId = () => {
    return Date.now().toString(36);
  };
  const addNewNode = (type: any) => {
    // Answer/Missed is auto-added and always allowed; every other node needs the flow name first.
    if (type !== 'answer' && type !== 'missed' && !flowName.trim()) {
      toast.warning('Please enter the IVR Flow Name first');
      return;
    }
    setNodes((prev: any) => {
      const isFirstNode = prev.length === 0;

      let finalPosition;
      if (isFirstNode) {
        finalPosition = { x: 100, y: 100 };
      } else if (flowRef.current) {
        const canvasEl = document.querySelector('.ivr-canvas-wrapper');
        const rect = canvasEl
          ? canvasEl.getBoundingClientRect()
          : { left: 0, top: 0, width: 1000, height: 600 };
        const screenCenter = {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
        };
        const flowPos = flowRef.current.screenToFlowPosition(screenCenter);
        const jitter = (prev.length % 5) * 30 - 60;
        finalPosition = { x: flowPos.x - 105 + jitter, y: flowPos.y - 40 };
      } else {
        const last = prev[prev.length - 1];
        finalPosition = {
          x: (last?.position?.x ?? 400) + 50,
          y: (last?.position?.y ?? 100) + 50,
        };
      }

      if (type === 'gather') {
        setIsGatherNode((c) => c + 1);
      }

      return [
        ...prev,
        {
          id: generateTimeId(),
          type: `${type}Node`,
          position: finalPosition,
          data: {
            label: `${type} Node`,
            verb:
              type === 'gather' ? 'dtmf' : type === 'aiAgent' ? 'ai_agent' : type,
            userId: loggedInUser,
            apiPath: BACKEND_API_BASE_URL,
            onRemove,
            ...(type === 'conditional' ? { conditions: [] } : {}),
            ...(type === 'aiAgent' ? { completionTypes: [] } : {}),
          },
        },
      ];
    });
  };

  // Ref keeps the keyboard listener (registered once) calling the latest
  // addNewNode/isPaletteActionAllowed closures without re-binding every render.
  const shortcutHandlersRef = useRef<any>();
  shortcutHandlersRef.current = { addNewNode, isPaletteActionAllowed };

  useEffect(() => {
    const handleKeyDown = (e: any) => {
      if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      const type = NODE_SHORTCUT_MAP[e.code];
      if (!type) return;

      const active: any = document.activeElement;
      const tag = active?.tagName;
      if (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        active?.isContentEditable
      )
        return;
      if (document.querySelector('.ivr-drawer-backdrop, .modal.show')) return;

      e.preventDefault();
      const {
        addNewNode: addNode,
        isPaletteActionAllowed: checkAllowed,
      } = shortcutHandlersRef.current;
      if (!checkAllowed(type)) {
        toast.warning(
          `You don't have permission to add a ${
            NODE_PALETTE_CFG[type]?.label || type
          } node.`,
        );
        return;
      }
      addNode(type);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const tidyLayout = useCallback(() => {
    const g: any = new dagre.graphlib.Graph();
    g.setDefaultEdgeLabel(() => ({}));
    g.setGraph({
      rankdir: 'TB',
      nodesep: 80,
      ranksep: 100,
      marginx: 30,
      marginy: 30,
    });

    const nodeHeights: any = {};
    nodes.forEach((node: any) => {
      let h = 80;
      if (node.type === 'gatherNode') {
        const keys = Object.keys(node.data?.selectedHandles || {}).length;
        const hasDefault = Number(node.data?.numDigits) !== 1;
        h = Math.max(80, 75 + keys * 26 + (hasDefault ? 26 : 0));
      } else if (node.type === 'conditionalNode') {
        h = Math.max(
          80,
          75 + normalizeConditionGroups(node.data?.conditions).length * 22,
        );
      } else if (node.type === 'aiAgentNode') {
        h = Math.max(80, 75 + (node.data?.completionTypes?.length || 0) * 26);
      }
      nodeHeights[node.id] = h;
      g.setNode(node.id, { width: 220, height: h });
    });

    getAcyclicLayoutEdges(nodes, edges).forEach((edge: any) =>
      g.setEdge(edge.source, edge.target),
    );
    dagre.layout(g);

    setNodes((nds: any[]) =>
      nds.map((node: any) => {
        const pos = g.node(node.id);
        if (!pos) return node;
        return {
          ...node,
          position: {
            x: pos.x - 110,
            y: pos.y - nodeHeights[node.id] / 2,
          },
        };
      }),
    );

    setTimeout(() => {
      flowRef.current?.fitView({ padding: 0.3, duration: 500 });
    }, 60);
  }, [nodes, edges, setNodes]);

  useEffect(() => {
    let alive = true;
    fetchIsBackendDev().then((isDev: any) => {
      if (alive) setIsBackendDev(isDev);
    });
    return () => {
      alive = false;
    };
  }, []);

  // Close export menu when clicking outside
  useEffect(() => {
    if (!showExportMenu) return;
    const close = () => setShowExportMenu(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [showExportMenu]);

  const NODE_COLORS_MAP: any = {
    answerNode: '#059669',
    playNode: '#2563eb',
    gatherNode: '#7c3aed',
    transferNode: '#d97706',
    hangupNode: '#dc2626',
    recordNode: '#0891b2',
    voicemailNode: '#4f46e5',
    missedNode: '#9333ea',
    defaultNode: '#475569',
    apiNode: '#b45309',
    conditionalNode: '#c026d3',
    aiAgentNode: '#0284c7',
  };
  const NODE_LABEL_MAP: any = {
    answerNode: 'Answer',
    playNode: 'Play Audio',
    gatherNode: 'DTMF/Gather',
    transferNode: 'Transfer',
    hangupNode: 'Hangup',
    recordNode: 'Record',
    voicemailNode: 'Voicemail',
    missedNode: 'Missed Call',
    defaultNode: 'Default Action',
    apiNode: 'API Webhook',
    conditionalNode: 'Conditional',
    aiAgentNode: 'AI Agent',
  };

  const downloadFlowJson = () => {
    setShowExportMenu(false);
    downloadIvrFlowJson({
      nodes,
      edges,
      flowName,
      isGatherNode,
      scheduleData,
      userId: cid,
      smsWebhookFlag,
      isMissed,
    });
  };

  const downloadFlowchart = async () => {
    setShowExportMenu(false);
    if (!flowRef.current) {
      toast.error('Canvas not ready');
      return;
    }

    const rfNodes = flowRef.current.getNodes();
    if (!rfNodes.length) {
      toast.error('No nodes to export');
      return;
    }

    const viewportEl: any = document.querySelector(
      '.ivr-canvas-wrapper .react-flow__viewport',
    );
    if (!viewportEl) {
      toast.error('Canvas not found');
      return;
    }

    // Calculate bounding box of all nodes in flow coordinates
    const pad = 60;
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    rfNodes.forEach((n: any) => {
      minX = Math.min(minX, n.position.x);
      minY = Math.min(minY, n.position.y);
      maxX = Math.max(maxX, n.position.x + (n.width || 220));
      maxY = Math.max(maxY, n.position.y + (n.height || 90));
    });
    const flowW = maxX - minX + pad * 2;
    const flowH = maxY - minY + pad * 2;

    const origTransform = viewportEl.style.transform;

    const fObjs = document.querySelectorAll(
      '.ivr-canvas-wrapper .react-flow__edge foreignObject',
    );
    fObjs.forEach((el: any) => {
      el.style.visibility = 'hidden';
    });

    const edgePaths = document.querySelectorAll(
      '.ivr-canvas-wrapper .react-flow__edge-path',
    );
    edgePaths.forEach((p: any) => {
      p.setAttribute('stroke', '#b0b7c3');
      p.setAttribute('stroke-width', '1.5');
    });

    viewportEl.style.transform = `translate(${pad - minX}px, ${
      pad - minY
    }px) scale(1)`;

    await new Promise((r) =>
      requestAnimationFrame(() => requestAnimationFrame(r)),
    );

    try {
      const { toPng } = await import('html-to-image');
      const dataUrl = await toPng(viewportEl, {
        backgroundColor: '#f1f5f9',
        width: flowW,
        height: flowH,
        pixelRatio: 2,
      });
      const a = document.createElement('a');
      a.download = `${flowName || 'ivr-flow'}-chart.png`;
      a.href = dataUrl;
      a.click();
      toast.success('Flowchart downloaded');
    } catch (err) {
      console.error(err);
      toast.error('Image export failed');
    } finally {
      viewportEl.style.transform = origTransform;
      fObjs.forEach((el: any) => {
        el.style.visibility = '';
      });
    }
  };

  const downloadDocumentation = () => {
    setShowExportMenu(false);
    const getHandleLabel = (edge: any) => {
      if (!edge.sourceHandle) return '';
      const m = edge.sourceHandle.match(/_handle-(\d+)$/);
      if (m) {
        const k = DTMF_KEYS[Number(m[1])];
        return k !== undefined ? ` (Key ${k})` : '';
      }
      if (edge.sourceHandle.includes('_single_source')) return ' (Default)';
      const cond = edge.sourceHandle.match(/_cond_(.+)$/);
      if (cond) {
        const src: any = nodes.find((n: any) => n.id === edge.source);
        const gi = normalizeConditionGroups(src?.data?.conditions).findIndex(
          (g: any) => g.groupId === cond[1],
        );
        return gi >= 0 ? ` (${conditionBranchLabel(gi)})` : '';
      }
      if (edge.sourceHandle.endsWith('_if')) return ' (If)';
      if (edge.sourceHandle.endsWith('_else')) return ' (Else)';
      const ct = edge.sourceHandle.match(/_ct_(.+)$/);
      if (ct) return ` (${decodeURIComponent(ct[1])})`;
      return '';
    };
    const getDetails = (node: any) => {
      const d = node.data || {};
      switch (node.type) {
        case 'answerNode':
          return d.webhookConfig
            ? `Webhook: ${d.webhookConfig.webhook_name || 'Configured'}`
            : 'No webhook';
        case 'playNode':
          return d.action?.name
            ? `Audio: ${d.action.name} (${d.action.duration || 0}s)`
            : 'Not configured';
        case 'gatherNode': {
          const keys = Object.entries(d.selectedHandles || {})
            .sort(
              ([a]: any, [b]: any) =>
                Number(a.split('-')[1]) - Number(b.split('-')[1]),
            )
            .map(([k]: any) => DTMF_KEYS[Number(k.split('-')[1])]);
          return `Digits: ${d.numDigits || 1} | Timeout: ${
            d.timeout || '?'
          }s | Keys: ${keys.join(', ') || 'None'}`;
        }
        case 'transferNode':
          return `Time slots: ${(d.timeSlots || []).length}`;
        case 'hangupNode':
          return 'Ends the call';
        case 'recordNode':
          return `Max duration: ${d.maxDuration || '?'}s`;
        case 'voicemailNode':
          return `Max duration: ${d.max_duration || '?'}s | Audio: ${
            d.action?.name || '—'
          }`;
        case 'missedNode':
          return `Mode: ${d.mode || 'Not set'} | Action: ${
            d.action?.name || '—'
          }`;
        case 'defaultNode':
          return `Mode: ${d.mode || 'Not set'}`;
        case 'apiNode':
          return d.url ? `${d.method || 'GET'} ${d.url}` : 'Not configured';
        case 'conditionalNode':
          return `Conditions: ${(d.conditions || []).length}`;
        case 'aiAgentNode':
          return d.aiAgentName
            ? `Agent: ${d.aiAgentName} | Completion types: ${(d.completionTypes || []).length}`
            : 'Not configured';
        default:
          return '—';
      }
    };
    const connMap: any = {};
    edges.forEach((e: any) => {
      if (!connMap[e.source]) connMap[e.source] = [];
      const t: any = nodes.find((n: any) => n.id === e.target);
      connMap[e.source].push(
        `${NODE_LABEL_MAP[t?.type] || '?'}${getHandleLabel(e)}`,
      );
    });
    const rows = nodes
      .map(
        (node: any, i: number) => `
            <tr>
                <td style="text-align:center;color:#64748b">${i + 1}</td>
                <td><span style="background:${
                  NODE_COLORS_MAP[node.type] || '#6b7280'
                };color:#fff;padding:2px 9px;border-radius:10px;font-size:11px;white-space:nowrap">${
          NODE_LABEL_MAP[node.type] || node.type
        }</span></td>
                <td style="font-family:monospace;font-size:11px;color:#64748b">${
                  node.id
                }</td>
                <td>${getDetails(node)}</td>
                <td>${
                  (connMap[node.id] || [])
                    .map((c: any) => `<div>→ ${c}</div>`)
                    .join('') || '—'
                }</td>
            </tr>`,
      )
      .join('');
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>IVR: ${flowName}</title>
<style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;margin:0;color:#1e293b}
.header{background:linear-gradient(135deg,#6366f1,#4f46e5);color:#fff;padding:32px 40px}
.header h1{margin:0 0 6px;font-size:22px}.header p{margin:3px 0;font-size:13px;opacity:.85}
.content{padding:32px 40px}
table{width:100%;border-collapse:collapse;font-size:13px;margin-top:16px}
th{background:#f1f5f9;padding:10px 12px;text-align:left;border:1px solid #e2e8f0;font-weight:600;color:#475569}
td{padding:9px 12px;border:1px solid #e2e8f0;vertical-align:top;color:#374151}
tr:nth-child(even) td{background:#f8fafc}
.print-btn{display:inline-block;margin-bottom:20px;padding:9px 20px;background:#6366f1;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:13px;font-family:inherit}
.print-btn:hover{background:#4f46e5}
@media print{.print-btn{display:none}}</style></head><body>
<div class="header"><h1>IVR Flow Documentation</h1>
<p><strong>Flow:</strong> ${flowName || 'Untitled'}</p>
<p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
<p><strong>Nodes:</strong> ${nodes.length} &nbsp;|&nbsp; <strong>Connections:</strong> ${
      edges.length
    }</p></div>
<div class="content"><button class="print-btn" onclick="window.print()">🖨️&nbsp; Print / Save as PDF</button>
<table><thead><tr><th>#</th><th>Type</th><th>Node ID</th><th>Configuration</th><th>Connects To</th></tr></thead>
<tbody>${rows}</tbody></table></div></body></html>`;
    const w: any = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
  };

  const updateNodeData = useCallback(
    (nodeId: any, updatedData: any) => {
      setNodes((nds: any[]) => {
        const prevNode: any = nds.find((n: any) => n.id === nodeId);
        const prevDigits = Number(prevNode?.data?.numDigits ?? 1);
        const nextDigits =
          updatedData.numDigits !== undefined
            ? Number(updatedData.numDigits)
            : prevDigits;

        if (prevDigits !== nextDigits) {
          setEdges((eds: any[]) =>
            eds.filter((edge: any) => {
              if (edge.source !== nodeId) return true;

              // multi â†’ single
              if (prevDigits > 1 && nextDigits === 1) {
                return edge.sourceHandle !== `${nodeId}_single_source`;
              }

              // single â†’ multi
              if (prevDigits === 1 && nextDigits > 1) {
                return !edge.sourceHandle?.startsWith(`${nodeId}_handle-`);
              }

              return true;
            }),
          );
        }

        const removedHandles: any[] = [];
        if (updatedData.selectedHandles && prevNode?.data?.selectedHandles) {
          Object.keys(prevNode.data.selectedHandles).forEach((hid: any) => {
            if (!updatedData.selectedHandles.hasOwnProperty(hid)) {
              removedHandles.push(hid);
            }
          });
        }

        if (removedHandles.length > 0) {
          setEdges((eds: any[]) =>
            eds.filter(
              (edge: any) =>
                !(
                  edge.source === nodeId &&
                  removedHandles.includes(edge.sourceHandle)
                ),
            ),
          );
        }

        return nds.map((node: any) =>
          node.id === nodeId
            ? { ...node, data: { ...node.data, ...updatedData } }
            : node,
        );
      });
    },
    [setNodes, setEdges],
  );

  const onRemove = (id: any) => {
    setNodes((n: any[]) => n.filter((x: any) => x.id !== id));
    setEdges((e: any[]) =>
      e.filter((x: any) => x.source !== id && x.target !== id),
    );
    scheduleDraftSave();
  };

  const setParentForTarget = useCallback(
    (targetId: any, parentLabel: any, idToRemove?: any) => {
      setNodes((nds: any[]) =>
        nds.map((n: any) =>
          n.id === targetId
            ? {
                ...n,
                data: { ...n.data, parent: parentLabel, idToRemove },
              }
            : n,
        ),
      );
    },
    [],
  );

  // Creates the draft row the first time a flow name exists (guarded against double-create).
  const createDraftIVR = useCallback(async () => {
    const trimmedName = flowName.trim();
    if (!trimmedName || draftId || draftCreateInFlightRef.current) return;
    draftCreateInFlightRef.current = true;
    try {
      const url = API_IVR_DRAFT_CREATE.replace(':cid', cid);
      const res: any = await postApiCall(url, {
        flowName: trimmedName,
        userId: cid,
        nodeData: '[]',
        edgesData: '[]',
        ivr_category: isMissed ? 'missed' : 'normal',
      });
      const newDraftId =
        res?.data?.flowJsonData?.flow_id ||
        res?.data?.flow_id ||
        res?.data?.id ||
        res?.flowJsonData?.flow_id;
      const newScheduleId =
        res?.data?.flowJsonData?.schedule_id ||
        res?.data?.schedule_id ||
        res?.flowJsonData?.schedule_id ||
        null;
      if (newDraftId) setDraftId(newDraftId);
      if (newScheduleId) setDraftScheduleId(newScheduleId);
    } catch (err) {
      // Silent — draft autosave must never block the user from working.
      console.error('IVR draft create failed', err);
    } finally {
      draftCreateInFlightRef.current = false;
    }
  }, [flowName, draftId, cid, isMissed]);

  // Syncs the current graph to the draft row — no validateIVR on purpose, flow may be incomplete.
  const saveDraft = useCallback(async () => {
    if (!draftId) return;
    try {
      const data = transformNodes({
        edges,
        flowName,
        nodesArray: nodes,
        isGatherNode,
        scheduleData,
        userId: cid,
        smsWebhookFlag,
        isMissed,
      });
      const url = API_IVR_DRAFT_UPDATE.replace(':cid', cid).replace(':id', draftId);
      await postApiCall(url, {
        nodeData: JSON.stringify(data),
        edgesData: JSON.stringify(edges),
        flowName,
        userId: cid,
        id: draftId,
        is_gather_node: isGatherNode,
        promptDuration: data.promptDuration,
        scheduleData,
        ...(draftScheduleId ? { schedule_id: draftScheduleId } : {}),
        ivr_category: isMissed ? 'missed' : 'normal',
      });
    } catch (err) {
      console.error('IVR draft autosave failed', err);
    }
  }, [
    draftId,
    draftScheduleId,
    edges,
    flowName,
    nodes,
    isGatherNode,
    scheduleData,
    cid,
    smsWebhookFlag,
    isMissed,
  ]);

  // Ref-based so the debounced timeout always calls the latest saveDraft, not a stale closure.
  const saveDraftRef = useRef(saveDraft);
  saveDraftRef.current = saveDraft;

  const scheduleDraftSave = useCallback(() => {
    if (draftSaveTimeoutRef.current) clearTimeout(draftSaveTimeoutRef.current);
    draftSaveTimeoutRef.current = setTimeout(() => {
      saveDraftRef.current();
    }, 800);
  }, []);

  useEffect(() => {
    return () => {
      if (draftSaveTimeoutRef.current) clearTimeout(draftSaveTimeoutRef.current);
    };
  }, []);

  const addFlowEdge = useCallback(
    (edgeConfig: any) => {
      setEdges((eds: any[]) => addEdge(edgeConfig, eds));
      scheduleDraftSave();
    },
    [scheduleDraftSave],
  );

  // Autosave on actual node/edge removals too (not drag/position/selection changes).
  const handleNodesChange = useCallback(
    (changes: any[]) => {
      onNodesChange(changes);
      if (changes.some((c: any) => c.type === 'remove')) {
        scheduleDraftSave();
      }
    },
    [onNodesChange, scheduleDraftSave],
  );

  const handleEdgesChange = useCallback(
    (changes: any[]) => {
      onEdgesChange(changes);
      if (changes.some((c: any) => c.type === 'remove')) {
        scheduleDraftSave();
      }
    },
    [onEdgesChange, scheduleDraftSave],
  );

  const onConnect = useCallback(
    (connection: any) => {
      const { source, target, sourceHandle } = connection;
      const sourceNode: any = nodes.find((n: any) => n.id === source);
      if (!sourceNode) return;
      const sourceVerb = sourceNode.data?.verb;

      const aiBranchError = aiBranchConnectError(source, target, nodes, edges);
      if (aiBranchError) {
        toast.warning(aiBranchError);
        return {};
      }

      const baseEdge = {
        ...connection,
        type: 'custom',
        markerEnd: { type: MarkerType.Arrow },
      };

      if (sourceVerb === 'dtmf') {
        const { numDigits = 1 } = sourceNode.data;
        if (Number(numDigits) === 1) {
          if (nodeConnections[sourceHandle]?.length > 0) {
            toast.warning('Each DTMF option can connect only once');
            return {};
          }
          const handleIndex = Number(sourceHandle?.split('-')[1]);
          const label = DTMF_KEYS[handleIndex];
          addFlowEdge({
            ...baseEdge,
            label: `dtmf: ${label}`,
            labelStyle: { fill: 'blue', fontWeight: 'bold' },
            data: {
              label: `dtmf: ${label}`,
            },
          });

          const idToRemove = `${sourceNode.id}_handle-${label}`;
          setParentForTarget(target, sourceNode.data.label, idToRemove);
          return;
        } else {
          if (nodeConnections[source]?.length >= 1) {
            toast.warning('This node can only have one next step');
            return;
          }

          addFlowEdge(baseEdge);
          setParentForTarget(target, sourceNode.data.label);
          return;
        }
      }

      if (sourceVerb === 'conditional') {
        if (nodeConnections[sourceHandle]?.length > 0) {
          toast.warning('Each branch (If / Else If / Else) can connect only once');
          return {};
        }
        addFlowEdge(baseEdge);
        setParentForTarget(target, sourceNode.data.label);
        return;
      }

      if (sourceVerb === 'ai_agent') {
        if (nodeConnections[sourceHandle]?.length > 0) {
          toast.warning('Each completion type can connect only once');
          return {};
        }
        addFlowEdge(baseEdge);
        setParentForTarget(target, sourceNode.data.label);
        return;
      }

      if (
        sourceVerb !== 'gather' &&
        (nodeConnections[source]?.length || 0) >= 1
      ) {
        toast.warning('A node can only have one child!');
        return {};
      }
      addFlowEdge(baseEdge);
      setParentForTarget(target, sourceNode.data.label);

      // When API node with "Play In Next Node" connects to a play node, propagate immediately
      if (
        sourceNode.type === 'apiNode' &&
        sourceNode.data?.action?.play_in_next_node
      ) {
        const targetNode: any = nodes.find((n: any) => n.id === target);
        if (targetNode?.type === 'playNode') {
          setNodes((nds: any[]) =>
            nds.map((n: any) =>
              n.id === target
                ? { ...n, data: { ...n.data, passToNextNode: true } }
                : n,
            ),
          );
        }
      }
    },
    [nodes, edges, nodeConnections, addFlowEdge, setParentForTarget, setNodes],
  );

  const saveData = async () => {
    try {
      const invalidIds = new Set();
      const isValid = validateIVR({
        edges,
        flowName,
        nodes,
        scheduleData,
        isMissed,
        onInvalidNode: (nid: any) => invalidIds.add(nid),
      });
      setNodes((nds: any[]) =>
        nds.map((n: any) => {
          const cls = String(n.className || '')
            .split(/\s+/)
            .filter((c: any) => c && c !== 'ivr-node-error');
          if (invalidIds.has(n.id)) cls.push('ivr-node-error');
          return { ...n, className: cls.join(' ') };
        }),
      );
      if (!isValid) return;
      // Cancel any in-flight debounced draft sync — the real save below supersedes it.
      if (draftSaveTimeoutRef.current) clearTimeout(draftSaveTimeoutRef.current);
      const data = transformNodes({
        edges,
        flowName,
        nodesArray: nodes,
        isGatherNode,
        scheduleData,
        userId: cid,
        smsWebhookFlag,
        isMissed,
      });
      const payload: any = {
        nodeData: JSON.stringify(data),
        edgesData: JSON.stringify(edges),
        flowName,
        userId: cid,
        is_gather_node: isGatherNode,
        promptDuration: data.promptDuration,
        scheduleData: scheduleData,
        ivr_category: isMissed ? 'missed' : 'normal',
      };

      // Draft already exists → finalize via update, not create (avoids the duplicate-name check).
      let apiIvrUrl = API_IVR_CREATE.replace(':cid', cid);
      if (draftId) {
        apiIvrUrl = API_IVR_EDIT.replace(':cid', cid).replace(':id', draftId);
        payload.id = draftId;
      }
      setLoading(true);
      await postApiCall(apiIvrUrl, payload);
      toast.success('IVR Created Successfully');
      setLoading(false);
      setTimeout(() => {
        navigate(`/${ROUTES.IVR.LIST.replace(':cid', cid)}`);
      }, 1500);
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Something went wrong while creating IVR');
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (draftSaveTimeoutRef.current) clearTimeout(draftSaveTimeoutRef.current);
    setDraftId(null);
    setDraftScheduleId(null);
    setNodes([]);
    setEdges([]);
    setNodeConnections({});
    setFlowName('');
    setError('');
    setIsGatherNode(0);

    if (isMissed) {
      addNewNode('missed');
    } else {
      addNewNode('answer');
    }
  };

  const handleScheduleClick = () => {
    setTimeScheduleModel(true);

    if (scheduleData && Object.keys(scheduleData).length > 0) {
      setStartTime(scheduleData.start_time);
      setEndTime(scheduleData.end_time);
      setWeekDays(scheduleData.week_days);

      if (scheduleData.week_days.length === 7) {
        setIsWholeWeek(true);
      } else {
        setIsWholeWeek(false);
      }

      if (
        scheduleData.start_time === DEFAULT_SCHEDULE_START &&
        scheduleData.end_time === DEFAULT_SCHEDULE_END
      ) {
        setIsWholeDay(true);
      } else {
        setIsWholeDay(false);
      }
    } else {
      setStartTime(DEFAULT_SCHEDULE_START);
      setEndTime(DEFAULT_SCHEDULE_END);
      setWeekDays([...WEEK_DAYS]);
      setIsWholeDay(true);
      setIsWholeWeek(true);
    }
  };

  const handleWholeDayChange = (e: any) => {
    const isChecked = e.target.checked;
    setIsWholeDay(isChecked);
    if (isChecked) {
      setPreviousTime({ start: startTime, end: endTime });
      setStartTime(DEFAULT_SCHEDULE_START);
      setEndTime(DEFAULT_SCHEDULE_END);
    } else {
      setStartTime(previousTime.start || '');
      setEndTime(previousTime.end || '');
    }
  };

  const handleWholeWeekChange = (e: any) => {
    const isChecked = e.target.checked;
    setIsWholeWeek(isChecked);
    if (isChecked) {
      setPreviousWeekDays(weekDays);
      setWeekDays(WEEK_DAYS as any);
    } else {
      setWeekDays(previousWeekDays || []);
    }
  };

  const toMinutes = (timeValue: any) => {
    if (!timeValue || !String(timeValue).includes(':')) return NaN;
    const [hours, minutes] = String(timeValue).split(':').map(Number);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return NaN;
    return hours * 60 + minutes;
  };

  const handleSetSchedule = () => {
    if (weekDays.length === 0) {
      toast.warning('Please fill all fields');
      return;
    }

    if (!isWholeDay) {
      if (!startTime || !endTime) {
        toast.warning('Please select both start time and end time');
        return;
      }

      const startMins = toMinutes(startTime);
      const endMins = toMinutes(endTime);

      if (!Number.isFinite(startMins) || !Number.isFinite(endMins)) {
        toast.warning('Invalid time selected');
        return;
      }

      if (endMins <= startMins) {
        toast.warning('End time should be greater than start time');
        return;
      }
    }

    setScheduleData({
      start_time: startTime,
      end_time: endTime,
      week_days: weekDays,
    });
    setTimeScheduleModel(false);
  };

  return (
    <>
      <NodesContext.Provider
        value={
          {
            nodes,
            setNodes,
            smsWebhookFlag,
            smsWebhookList,
            webhookList,
            apiWebhookList,
            flowVariablesList,
            flowOperatorsList,
            updateNodeData,
            onRemove,
            audioList,
            nodeConnections,
          } as any
        }
      >
        <div
          className={`ivr-editor-page${isLocked ? ' ivr-editor-locked' : ''}`}
          style={{
            height: 'calc(100vh / var(--app-zoom, 1))',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* ── Flash alert ── */}
          {alertMessage && (
            <div
              className="border-b border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-sm text-emerald-700 dark:text-emerald-400"
              role="status"
            >
              <span className="ivr-alert-marquee">
                <span
                  className="ivr-alert-marquee-text"
                  style={{ '--scroll-dur': `${Math.max(18, alertMessage.length * 0.16)}s` } as any}
                >
                  {alertMessage}
                </span>
              </span>
            </div>
          )}

          {/* â”€â”€ Top toolbar â”€â”€ */}
          <div className="ivr-toolbar-v2 max-lg:!h-auto max-lg:!flex-wrap max-lg:!py-2">
            <div className="ivr-name-wrap">
              <input
                className="ivr-name-input"
                value={flowName}
                onChange={(e) => {
                  const value = e.target.value;
                  setFlowName(value);
                  const errs = [];
                  if (/\s/.test(value)) errs.push('No spaces');
                  if (/\d/.test(value)) errs.push('No numbers');
                  if (/[^a-zA-Z0-9\s]/.test(value)) errs.push('No special chars');
                  setError(errs.length ? errs.join(' · ') : '');
                }}
                onBlur={() => {
                  if (!error) createDraftIVR();
                }}
                placeholder="IVR Flow Name"
              />
              {error && (
                <span className="ivr-toolbar-error">
                  <AlertTriangle size={12} strokeWidth={2} /> {error}
                </span>
              )}
            </div>

            {(() => {
              const answerNode: any = nodes.find(
                (n: any) => n.type === 'answerNode',
              );
              if (!answerNode) return null;
              const names = answerNode?.data?.languageNames;
              const hasLangs = Array.isArray(names) && names.length > 0;
              const text = hasLangs
                ? `✓  ${names.join('  ·  ')}`
                : '⚠   No language selected in Answer node';
              const dur = `${Math.max(6, text.length * 0.22)}s`;
              return (
                <span
                  className={`ivr-lang-marquee ivr-lang-marquee--${
                    hasLangs ? 'green' : 'red'
                  }`}
                >
                  <span
                    className="ivr-lang-marquee-text"
                    style={{ '--scroll-dur': dur } as any}
                  >
                    {text}
                  </span>
                </span>
              );
            })()}

            {scheduleData && Object.keys(scheduleData).length > 0 && (
              <span className="ivr-schedule-badge">
                <Clock size={11} strokeWidth={2} />
                {scheduleData.start_time}–{scheduleData.end_time}
              </span>
            )}

            <div style={{ flex: 1 }} />

            <button
              className="ivr-toolbar-btn"
              onClick={() => setShowShortcutsModal(true)}
              title="Keyboard shortcuts"
              aria-label="Keyboard shortcuts"
            >
              <Info size={14} strokeWidth={2} />
            </button>

            <button
              className="ivr-toolbar-btn ivr-toolbar-btn--tidy"
              onClick={tidyLayout}
              title="Auto-arrange nodes"
            >
              <Wand2 size={12} strokeWidth={2} /> Tidy
            </button>

            {/* Export dropdown */}
            <div
              style={{ position: 'relative' }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <button
                className="ivr-toolbar-btn ivr-toolbar-btn--export"
                onClick={() => setShowExportMenu((v) => !v)}
              >
                <Download size={12} strokeWidth={2} /> Export{' '}
                <ChevronDown size={12} strokeWidth={2} />
              </button>
              {showExportMenu && (
                <div className="ivr-export-menu">
                  <button onClick={downloadFlowchart}>
                    <ImageIcon size={12} strokeWidth={2} /> Flowchart (PNG)
                  </button>
                  <button onClick={downloadDocumentation}>
                    <FileText size={12} strokeWidth={2} /> Documentation (PDF)
                  </button>
                  {isBackendDev && (
                    <button
                      onClick={downloadFlowJson}
                      title="Development only"
                    >
                      <Code2 size={12} strokeWidth={2} /> Flow JSON
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Lock toggle */}
            <button
              className={`ivr-toolbar-btn ${
                isLocked ? 'ivr-toolbar-btn--locked' : 'ivr-toolbar-btn--lock'
              }`}
              onClick={() => setIsLocked((v) => !v)}
              title={isLocked ? 'Click to unlock' : 'Lock flow'}
            >
              {isLocked ? (
                <Lock size={12} strokeWidth={2} />
              ) : (
                <LockOpen size={12} strokeWidth={2} />
              )}
              {isLocked ? ' Locked' : ' Lock'}
            </button>

            <button
              className="ivr-toolbar-btn ivr-toolbar-btn--schedule"
              onClick={handleScheduleClick}
            >
              <CalendarIcon size={12} strokeWidth={2} /> Schedule
            </button>

            <button
              className="ivr-toolbar-btn ivr-toolbar-btn--save"
              onClick={saveData}
              disabled={Boolean(error) || loading}
            >
              {loading ? (
                <>
                  <Loader2 size={12} strokeWidth={2} className="animate-spin" />{' '}
                  Saving…
                </>
              ) : (
                <>
                  <Save size={12} strokeWidth={2} /> Save IVR
                </>
              )}
            </button>
          </div>

          {/* â”€â”€ Main body â”€â”€ */}
          <div className="ivr-main-body max-md:flex-col">
            {/* Left palette sidebar */}
            <div className="ivr-palette max-md:!max-h-[35%] max-md:!w-full max-md:border-b">
              <div className="ivr-palette-header">Widget Library</div>
              <div className="ivr-palette-subtitle">
                Drop the widgets into the builder
              </div>
              <div className="ivr-palette-grid max-md:!grid-cols-4">
                {Object.keys(actions)
                  .filter((type: any) =>
                    isMissed ? type === 'missed' : type !== 'missed',
                  )
                  .filter((type: any) => isPaletteActionAllowed(type))
                  .map((type: any) => {
                    const cfg = NODE_PALETTE_CFG[type] || {
                      Icon: null,
                      color: '#6b7280',
                      label: actions[type],
                      hint: '',
                    };
                    const isAnswer = actions[type] === 'Answer';
                    return (
                      <div
                        key={type}
                        className={`ivr-palette-item${
                          isAnswer ? ' ivr-palette-item--disabled' : ''
                        }`}
                        style={{ '--palette-accent': cfg.color } as any}
                        title={cfg.hint}
                        onClick={() => {
                          if (!isAnswer) addNewNode(type);
                        }}
                      >
                        <div
                          className="ivr-palette-item-icon"
                          style={{ background: cfg.color }}
                        >
                          {cfg.Icon && (
                            <cfg.Icon
                              size={20}
                              strokeWidth={2}
                              color="#fff"
                            />
                          )}
                        </div>
                        <span className="ivr-palette-item-name">
                          {cfg.label}
                        </span>
                      </div>
                    );
                  })}
              </div>
              <div className="ivr-palette-footer">
                <div className="ivr-palette-reset" onClick={handleReset}>
                  <RotateCcw size={12} strokeWidth={2} /> Reset All
                </div>
              </div>
            </div>

            {/* Canvas */}
            <div className="ivr-canvas-wrapper">
              <ReactFlowProvider>
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  onConnect={onConnect}
                  onNodesChange={handleNodesChange}
                  onEdgesChange={handleEdgesChange}
                  nodeTypes={nodeTypes as any}
                  edgeTypes={edgeTypes as any}
                  onInit={(rf: any) => {
                    flowRef.current = rf;
                  }}
                  fitView
                  fitViewOptions={{ padding: 0.4, maxZoom: 0.8 }}
                  minZoom={0.05}
                  maxZoom={2}
                  nodesDraggable={!isLocked}
                  nodesConnectable={!isLocked}
                  elementsSelectable={!isLocked}
                  connectOnClick={true}
                  connectionLineStyle={{
                    stroke: '#6366f1',
                    strokeWidth: 1.5,
                    strokeDasharray: '5 3',
                  }}
                  proOptions={{ hideAttribution: true }}
                  defaultEdgeOptions={{
                    type: 'custom',
                    markerEnd: {
                      type: MarkerType.ArrowClosed,
                      color: '#94a3b8',
                    },
                  }}
                >
                  <Background
                    variant={'dots' as any}
                    gap={20}
                    size={1.5}
                    color="#94a3b8"
                  />
                  <Controls showInteractive={false} />
                  <MiniMap
                    nodeColor={(node: any) => {
                      const cm: any = {
                        answerNode: '#059669',
                        playNode: '#2563eb',
                        gatherNode: '#7c3aed',
                        transferNode: '#d97706',
                        hangupNode: '#dc2626',
                        recordNode: '#0891b2',
                        voicemailNode: '#4f46e5',
                        missedNode: '#9333ea',
                        defaultNode: '#475569',
                        apiNode: '#b45309',
                      };
                      return cm[node.type] || '#6b7280';
                    }}
                    nodeStrokeWidth={0}
                    zoomable
                    pannable
                    position="bottom-right"
                  />
                </ReactFlow>
              </ReactFlowProvider>
            </div>
          </div>
        </div>
      </NodesContext.Provider>

      <Modal
        show={showShortcutsModal}
        onHide={() => setShowShortcutsModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem' }}>
            Keyboard Shortcuts
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: '13px' }}>
            Hold <kbd>Alt</kbd> and press the first letter of a widget to add it
            straight to the canvas — for example <kbd>Alt</kbd> + <kbd>P</kbd>{' '}
            adds a Play Audio node. Shortcuts are ignored while you are typing
            in a field or a panel is open.
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: '8px',
            }}
          >
            {NODE_SHORTCUT_LIST.filter(({ type }: any) =>
              isMissed ? type === 'missed' : type !== 'missed',
            )
              .filter(({ type }: any) => isPaletteActionAllowed(type))
              .map(({ type, keyLabel }: any) => {
                const cfg = NODE_PALETTE_CFG[type] || {};
                return (
                  <div
                    key={type}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 10px',
                      border: '1px solid rgba(148, 163, 184, 0.3)',
                      borderRadius: '8px',
                      fontSize: '13px',
                    }}
                  >
                    {cfg.Icon && (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '26px',
                          height: '26px',
                          borderRadius: '6px',
                          background: cfg.color,
                          flexShrink: 0,
                        }}
                      >
                        <cfg.Icon size={14} strokeWidth={2} color="#fff" />
                      </span>
                    )}
                    <span style={{ flex: 1 }}>{cfg.label || type}</span>
                    <span style={{ whiteSpace: 'nowrap' }}>
                      <kbd>Alt</kbd> + <kbd>{keyLabel}</kbd>
                    </span>
                  </div>
                );
              })}
          </div>
          <p className="mb-0 mt-3" style={{ fontSize: '12px' }}>
            DTMF uses <kbd>Alt</kbd> + <kbd>G</kbd> because <kbd>Alt</kbd> +{' '}
            <kbd>D</kbd> is reserved by the browser.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowShortcutsModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={timeScheduleModel}
        onHide={() => setTimeScheduleModel(false)}
        size="lg"
        centered
        backdrop={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Set Time Schedule</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col>
              <Form>
                <Form.Check
                  type="checkbox"
                  label="Whole Day"
                  id="wholeDay"
                  className="mb-3"
                  checked={isWholeDay}
                  onChange={handleWholeDayChange}
                />
                <Form.Group className="mb-3">
                  <Form.Label>
                    Start Time{' '}
                    <span style={{ color: 'red', margin: '0 4px' }}>*</span>
                  </Form.Label>
                  <CalendarTimeInput
                    value={startTime}
                    onChange={(e) => setStartTime(e)}
                    disabled={isWholeDay}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>
                    End Time{' '}
                    <span style={{ color: 'red', margin: '0 4px' }}>*</span>
                  </Form.Label>
                  <CalendarTimeInput
                    value={endTime}
                    onChange={(e) => setEndTime(e)}
                    disabled={isWholeDay}
                  />
                </Form.Group>
                <Form.Check
                  type="checkbox"
                  label="Whole Week"
                  id="wholeWeek"
                  className="mb-3"
                  checked={isWholeWeek}
                  onChange={handleWholeWeekChange}
                />
                <Form.Group className="mb-3">
                  <Form.Label>
                    Select Days
                    <span style={{ color: 'red', margin: '0 4px' }}>*</span>
                  </Form.Label>
                  <div>
                    {WEEK_DAYS.map((day: any) => (
                      <Form.Check
                        inline
                        key={day}
                        label={day}
                        type="checkbox"
                        checked={weekDays.includes(day)}
                        onChange={(e: any) => {
                          if (e.target.checked) {
                            setWeekDays((prev: any[]) => [...prev, day]);
                          } else {
                            setWeekDays((prev: any[]) =>
                              prev.filter((d: any) => d !== day),
                            );
                          }
                        }}
                        disabled={isWholeWeek}
                      />
                    ))}
                  </div>
                </Form.Group>
              </Form>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setTimeScheduleModel(false)}
          >
            Close
          </Button>
          <Button variant="primary" onClick={handleSetSchedule}>
            Set Schedule
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default NewAddIVR;
